<script lang="ts">
import { RouteNames } from "../../../../utils/constants.ts";

export function getRouteXAccountsFiatSend() {
  return {
    name: RouteNames.X_SEND_CASH,
  };
}
</script>
<script setup lang="ts">
import BreadCrumbs from "../../../../components/BreadCrumbs.vue";
import BasicCard from "../../../../components/BasicCard.vue";
import TextInput from "../../../../components/form/TextInput.vue";
import InputSelect from "../../../../components/form/InputSelect.vue";
import InputCheckbox from "../../../../components/form/InputCheckbox.vue";
import { ref, computed } from "vue";
import { 
  ArrowRight as ArrowRightIcon,
  Send as SendIcon,
  User as UserIcon,
  Check as CheckIcon,
  AlertTriangle as AlertTriangleIcon,
  Phone as PhoneIcon,
  Mail as MailIcon,
  Building as BuildingIcon
} from "lucide-vue-next";

// Available currencies
const currencies = {
  "USD": "US Dollar (USD)",
  "EUR": "Euro (EUR)",
  "GBP": "British Pound (GBP)",
  "CAD": "Canadian Dollar (CAD)",
  "AUD": "Australian Dollar (AUD)",
  "CHF": "Swiss Franc (CHF)",
  "JPY": "Japanese Yen (JPY)"
};

// Mock user accounts with balances
const userAccounts = {
  "checking_usd": "Checking Account (USD) - $5,420.50",
  "savings_usd": "Savings Account (USD) - $12,340.00",
  "checking_eur": "Checking Account (EUR) - €2,150.75",
  "business_usd": "Business Account (USD) - $25,680.00"
};

// Transfer methods
const transferMethods = {
  "bank_transfer": "Bank Transfer (ACH) - Free, 1-3 days",
  "wire_transfer": "Wire Transfer - $25 fee, Same day",
  "instant_transfer": "Instant Transfer - $2.99 fee, Instant",
  "international": "International Wire - $45 fee, 1-5 days"
};

// Form state
const fromAccount = ref("checking_usd");
const transferMethod = ref("bank_transfer");
const recipientType = ref("bank"); // "bank", "email", "phone"
const amount = ref("");
const currency = ref("USD");
const memo = ref("");

// Recipient details
const recipientName = ref("");
const recipientEmail = ref("");
const recipientPhone = ref("");
const bankName = ref("");
const accountNumber = ref("");
const routingNumber = ref("");
const swiftCode = ref("");
const iban = ref("");
const recipientAddress = ref("");

// International transfer fields
const recipientCountry = ref("US");
const purposeOfTransfer = ref("");

// Security and preferences
const requiresAuth = ref(true);
const sendNotification = ref(true);
const scheduleTransfer = ref(false);
const scheduledDate = ref("");

// UI state
const step = ref(1); // 1: details, 2: recipient, 3: review, 4: confirmation
const isLoading = ref(false);
const transactionId = ref("");

// Country options for international transfers
const countries = {
  "US": "United States",
  "CA": "Canada", 
  "GB": "United Kingdom",
  "DE": "Germany",
  "FR": "France",
  "IT": "Italy",
  "ES": "Spain",
  "AU": "Australia",
  "JP": "Japan",
  "CH": "Switzerland"
};

// Transfer purposes for international
const transferPurposes = {
  "family_support": "Family Support",
  "education": "Education",
  "business": "Business Transaction",
  "investment": "Investment",
  "personal": "Personal",
  "gift": "Gift",
  "other": "Other"
};

// Computed values
const isInternational = computed(() => {
  return transferMethod.value === "international";
});

const transferFee = computed(() => {
  const fees = {
    "bank_transfer": 0,
    "wire_transfer": 25,
    "instant_transfer": 2.99,
    "international": 45
  };
  return fees[transferMethod.value as keyof typeof fees] || 0;
});

const totalAmount = computed(() => {
  const sendAmount = parseFloat(amount.value) || 0;
  return sendAmount + transferFee.value;
});

const userBalance = computed(() => {
  const balances = {
    "checking_usd": 5420.50,
    "savings_usd": 12340.00,
    "checking_eur": 2150.75,
    "business_usd": 25680.00
  };
  return balances[fromAccount.value as keyof typeof balances] || 0;
});

const exchangeRate = computed(() => {
  // Mock exchange rates
  const rates = {
    "USD": 1,
    "EUR": 0.85,
    "GBP": 0.73,
    "CAD": 1.25,
    "AUD": 1.35,
    "CHF": 0.92,
    "JPY": 110
  };
  return rates[currency.value as keyof typeof rates] || 1;
});

// Validation
const validateStep1 = () => {
  const sendAmount = parseFloat(amount.value);
  return !isNaN(sendAmount) && sendAmount > 0 && sendAmount <= userBalance.value;
};

const validateStep2 = () => {
  if (recipientType.value === "email") {
    return recipientEmail.value && recipientName.value;
  } else if (recipientType.value === "phone") {
    return recipientPhone.value && recipientName.value;
  } else {
    const hasBasicInfo = recipientName.value && bankName.value;
    if (isInternational.value) {
      return hasBasicInfo && (swiftCode.value || iban.value) && recipientCountry.value;
    } else {
      return hasBasicInfo && accountNumber.value && routingNumber.value;
    }
  }
};

// Form handlers
const setQuickAmount = (quickAmount: number) => {
  amount.value = quickAmount.toString();
};

const setMaxAmount = () => {
  const maxSendable = userBalance.value - transferFee.value;
  amount.value = Math.max(0, maxSendable).toString();
};

const nextStep = () => {
  if (step.value === 1 && validateStep1()) {
    step.value = 2;
  } else if (step.value === 2 && validateStep2()) {
    step.value = 3;
  }
};

const previousStep = () => {
  if (step.value > 1) {
    step.value--;
  }
};

const confirmTransfer = async () => {
  isLoading.value = true;
  
  // Simulate API call
  setTimeout(() => {
    transactionId.value = "TRN" + Math.random().toString(36).substring(2, 11).toUpperCase();
    step.value = 4;
    isLoading.value = false;
  }, 3000);
};

const startNewTransfer = () => {
  step.value = 1;
  amount.value = "";
  memo.value = "";
  recipientName.value = "";
  recipientEmail.value = "";
  recipientPhone.value = "";
  bankName.value = "";
  accountNumber.value = "";
  routingNumber.value = "";
  swiftCode.value = "";
  iban.value = "";
  transactionId.value = "";
};

// Recent recipients (mock data)
const recentRecipients = [
  { name: "John Smith", email: "john@example.com", type: "email", lastUsed: "2 days ago" },
  { name: "Sarah Johnson", bank: "Chase Bank", account: "****1234", type: "bank", lastUsed: "1 week ago" },
  { name: "Mike Wilson", phone: "+1 (555) 123-4567", type: "phone", lastUsed: "3 days ago" }
];

const selectRecentRecipient = (recipient: any) => {
  recipientName.value = recipient.name;
  if (recipient.type === "email") {
    recipientType.value = "email";
    recipientEmail.value = recipient.email;
  } else if (recipient.type === "phone") {
    recipientType.value = "phone";
    recipientPhone.value = recipient.phone;
  } else if (recipient.type === "bank") {
    recipientType.value = "bank";
    bankName.value = recipient.bank;
  }
};
</script>

<template>
  <BreadCrumbs :title="$t('Send Cash')" class="mb-5" />

  <div class="max-w-2xl mx-auto">
    <!-- Step Indicator -->
    <div class="flex justify-center mb-8">
      <div class="flex items-center space-x-4">
        <div class="flex items-center">
          <div :class="[step >= 1 ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-600', 'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium']">
            1
          </div>
          <span class="ml-2 text-sm">Amount & Method</span>
        </div>
        <arrow-right-icon class="h-4 w-4 text-gray-400" />
        <div class="flex items-center">
          <div :class="[step >= 2 ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-600', 'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium']">
            2
          </div>
          <span class="ml-2 text-sm">Recipient</span>
        </div>
        <arrow-right-icon class="h-4 w-4 text-gray-400" />
        <div class="flex items-center">
          <div :class="[step >= 3 ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-600', 'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium']">
            3
          </div>
          <span class="ml-2 text-sm">Review</span>
        </div>
        <arrow-right-icon class="h-4 w-4 text-gray-400" />
        <div class="flex items-center">
          <div :class="[step >= 4 ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600', 'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium']">
            <check-icon v-if="step >= 4" class="h-4 w-4" />
            <span v-else>4</span>
          </div>
          <span class="ml-2 text-sm">Confirm</span>
        </div>
      </div>
    </div>

    <!-- Step 1: Amount & Method -->
    <BasicCard v-if="step === 1">
      <div class="space-y-6">
        <div class="flex items-center gap-3 mb-6">
          <send-icon class="h-6 w-6 text-blue-600" />
          <h2 class="text-xl font-semibold">Send Money</h2>
        </div>

        <form @submit.prevent="nextStep" class="space-y-6">
          <!-- From Account -->
          <div>
            <label class="text-sm text-gray-600 mb-2 block">From Account</label>
            <InputSelect 
              v-model="fromAccount" 
              :list="userAccounts"
              placeholder="Select account"
            />
            <p class="text-sm text-gray-500 mt-1">
              Available balance: ${{ userBalance.toLocaleString() }}
            </p>
          </div>

          <!-- Amount -->
          <div>
            <label class="text-sm text-gray-600 mb-2 block">Amount</label>
            <div class="flex gap-2 mb-3">
              <button
                v-for="quickAmount in [100, 500, 1000, 2500]"
                :key="quickAmount"
                type="button"
                @click="setQuickAmount(quickAmount)"
                class="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
              >
                ${{ quickAmount }}
              </button>
              <button
                type="button"
                @click="setMaxAmount"
                class="px-3 py-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors text-sm"
              >
                MAX
              </button>
            </div>
            <div class="flex gap-3">
              <div class="flex-1">
                <TextInput
                  v-model="amount"
                  name="amount"
                  type="number"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
              <div class="w-24">
                <InputSelect 
                  v-model="currency" 
                  :list="currencies"
                />
              </div>
            </div>
            <p v-if="amount && currency !== 'USD'" class="text-sm text-gray-500 mt-1">
              ≈ ${{ (parseFloat(amount) * exchangeRate).toFixed(2) }} USD
            </p>
          </div>

          <!-- Transfer Method -->
          <div>
            <label class="text-sm text-gray-600 mb-2 block">Transfer Method</label>
            <InputSelect 
              v-model="transferMethod" 
              :list="transferMethods"
              placeholder="Select transfer method"
            />
          </div>

          <!-- Memo -->
          <div>
            <label class="text-sm text-gray-600 mb-2 block">Memo (Optional)</label>
            <TextInput
              v-model="memo"
              name="memo"
              type="text"
              placeholder="What's this for?"
            />
          </div>

          <!-- Schedule Transfer -->
          <div>
            <InputCheckbox v-model="scheduleTransfer" name="schedule">
              Schedule this transfer for later
            </InputCheckbox>
            <div v-if="scheduleTransfer" class="mt-3">
              <TextInput
                v-model="scheduledDate"
                name="scheduledDate"
                type="date"
                label="Schedule Date"
              />
            </div>
          </div>

          <!-- Fee Summary -->
          <div v-if="amount" class="bg-gray-50 p-4 rounded-lg">
            <div class="flex justify-between text-sm mb-2">
              <span>Send Amount</span>
              <span>${{ parseFloat(amount).toFixed(2) }}</span>
            </div>
            <div class="flex justify-between text-sm mb-2">
              <span>Transfer Fee</span>
              <span>${{ transferFee.toFixed(2) }}</span>
            </div>
            <div class="border-t pt-2 flex justify-between font-medium">
              <span>Total</span>
              <span>${{ totalAmount.toFixed(2) }}</span>
            </div>
          </div>

          <!-- Validation -->
          <div v-if="amount && parseFloat(amount) > userBalance" class="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <alert-triangle-icon class="h-5 w-5 text-red-600" />
            <span class="text-sm text-red-800">Insufficient funds</span>
          </div>

          <!-- Action Button -->
          <div class="flex justify-end pt-4">
            <button
              type="submit"
              :disabled="!validateStep1()"
              class="px-8 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              Continue
            </button>
          </div>
        </form>
      </div>
    </BasicCard>

    <!-- Step 2: Recipient Details -->
    <BasicCard v-if="step === 2">
      <div class="space-y-6">
        <div class="flex items-center gap-3 mb-6">
          <user-icon class="h-6 w-6 text-green-600" />
          <h2 class="text-xl font-semibold">Recipient Information</h2>
        </div>

        <!-- Recent Recipients -->
        <div v-if="recentRecipients.length > 0">
          <h3 class="text-sm font-medium text-gray-800 mb-3">Recent Recipients</h3>
          <div class="grid gap-3 mb-6">
            <div 
              v-for="recipient in recentRecipients"
              :key="recipient.name"
              @click="selectRecentRecipient(recipient)"
              class="p-3 border border-gray-200 rounded-lg cursor-pointer hover:border-blue-300 hover:bg-blue-50 transition-all"
            >
              <div class="flex justify-between items-start">
                <div>
                  <p class="font-medium">{{ recipient.name }}</p>
                  <p class="text-sm text-gray-600">
                    <span v-if="recipient.type === 'email'">{{ recipient.email }}</span>
                    <span v-else-if="recipient.type === 'phone'">{{ recipient.phone }}</span>
                    <span v-else>{{ recipient.bank }} {{ recipient.account }}</span>
                  </p>
                </div>
                <span class="text-xs text-gray-500">{{ recipient.lastUsed }}</span>
              </div>
            </div>
          </div>
        </div>

        <form @submit.prevent="nextStep" class="space-y-6">
          <!-- Recipient Type -->
          <div>
            <label class="text-sm text-gray-600 mb-3 block">Send to</label>
            <div class="grid grid-cols-3 gap-3">
              <button
                type="button"
                @click="recipientType = 'bank'"
                :class="[
                  'p-4 border rounded-lg text-center transition-all',
                  recipientType === 'bank' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'
                ]"
              >
                <building-icon class="h-6 w-6 mx-auto mb-2" />
                <div class="text-sm font-medium">Bank Account</div>
              </button>
              <button
                type="button"
                @click="recipientType = 'email'"
                :class="[
                  'p-4 border rounded-lg text-center transition-all',
                  recipientType === 'email' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'
                ]"
              >
                <mail-icon class="h-6 w-6 mx-auto mb-2" />
                <div class="text-sm font-medium">Email</div>
              </button>
              <button
                type="button"
                @click="recipientType = 'phone'"
                :class="[
                  'p-4 border rounded-lg text-center transition-all',
                  recipientType === 'phone' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'
                ]"
              >
                <phone-icon class="h-6 w-6 mx-auto mb-2" />
                <div class="text-sm font-medium">Phone</div>
              </button>
            </div>
          </div>

          <!-- Basic Recipient Info -->
          <div>
            <TextInput
              v-model="recipientName"
              name="recipientName"
              type="text"
              label="Recipient Name"
              placeholder="Full name"
            />
          </div>

          <!-- Email Recipient -->
          <div v-if="recipientType === 'email'">
            <TextInput
              v-model="recipientEmail"
              name="recipientEmail"
              type="email"
              label="Email Address"
              placeholder="recipient@example.com"
            />
          </div>

          <!-- Phone Recipient -->
          <div v-if="recipientType === 'phone'">
            <TextInput
              v-model="recipientPhone"
              name="recipientPhone"
              type="tel"
              label="Phone Number"
              placeholder="+1 (555) 123-4567"
            />
          </div>

          <!-- Bank Account Recipient -->
          <div v-if="recipientType === 'bank'" class="space-y-4">
            <TextInput
              v-model="bankName"
              name="bankName"
              type="text"
              label="Bank Name"
              placeholder="Bank name"
            />

            <!-- International vs Domestic -->
            <div v-if="isInternational">
              <div class="grid grid-cols-2 gap-4">
                <TextInput
                  v-model="swiftCode"
                  name="swiftCode"
                  type="text"
                  label="SWIFT Code"
                  placeholder="SWIFT/BIC"
                />
                <InputSelect 
                  v-model="recipientCountry" 
                  :list="countries"
                  label="Country"
                />
              </div>
              <TextInput
                v-model="iban"
                name="iban"
                type="text"
                label="IBAN (if applicable)"
                placeholder="International Bank Account Number"
              />
              <TextInput
                v-model="recipientAddress"
                name="recipientAddress"
                type="text"
                label="Recipient Address"
                placeholder="Full address"
              />
              <InputSelect 
                v-model="purposeOfTransfer" 
                :list="transferPurposes"
                label="Purpose of Transfer"
                placeholder="Select purpose"
              />
            </div>

            <div v-else class="grid grid-cols-2 gap-4">
              <TextInput
                v-model="routingNumber"
                name="routingNumber"
                type="text"
                label="Routing Number"
                placeholder="9-digit routing number"
              />
              <TextInput
                v-model="accountNumber"
                name="accountNumber"
                type="text"
                label="Account Number"
                placeholder="Account number"
              />
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex justify-between pt-6">
            <button
              type="button"
              @click="previousStep"
              class="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Back
            </button>
            <button
              type="submit"
              :disabled="!validateStep2()"
              class="px-8 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              Continue
            </button>
          </div>
        </form>
      </div>
    </BasicCard>

    <!-- Step 3: Review Transfer -->
    <BasicCard v-if="step === 3">
      <div class="space-y-6">
        <div class="flex items-center gap-3 mb-6">
          <alert-triangle-icon class="h-6 w-6 text-orange-600" />
          <h2 class="text-xl font-semibold">Review Transfer</h2>
        </div>

        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div class="flex items-start gap-3">
            <alert-triangle-icon class="h-5 w-5 text-yellow-600 mt-0.5" />
            <div>
              <h3 class="font-medium text-yellow-800 mb-1">Review Carefully</h3>
              <p class="text-sm text-yellow-700">
                Please verify all details. Money transfers cannot be canceled once sent.
              </p>
            </div>
          </div>
        </div>

        <!-- Transfer Summary -->
        <div class="space-y-4">
          <div class="p-4 bg-gray-50 rounded-lg">
            <h3 class="font-medium text-gray-800 mb-3">Transfer Details</h3>
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span class="text-gray-600">From</span>
                <p class="font-medium">{{ userAccounts[fromAccount as keyof typeof userAccounts] }}</p>
              </div>
              <div>
                <span class="text-gray-600">Amount</span>
                <p class="font-medium">${{ amount }} {{ currency }}</p>
              </div>
              <div>
                <span class="text-gray-600">Method</span>
                <p class="font-medium">{{ transferMethods[transferMethod as keyof typeof transferMethods] }}</p>
              </div>
              <div>
                <span class="text-gray-600">Fee</span>
                <p class="font-medium">${{ transferFee.toFixed(2) }}</p>
              </div>
            </div>
          </div>

          <div class="p-4 bg-gray-50 rounded-lg">
            <h3 class="font-medium text-gray-800 mb-3">Recipient</h3>
            <div class="space-y-2 text-sm">
              <div>
                <span class="text-gray-600">Name: </span>
                <span class="font-medium">{{ recipientName }}</span>
              </div>
              <div v-if="recipientType === 'email'">
                <span class="text-gray-600">Email: </span>
                <span class="font-medium">{{ recipientEmail }}</span>
              </div>
              <div v-if="recipientType === 'phone'">
                <span class="text-gray-600">Phone: </span>
                <span class="font-medium">{{ recipientPhone }}</span>
              </div>
              <div v-if="recipientType === 'bank'">
                <span class="text-gray-600">Bank: </span>
                <span class="font-medium">{{ bankName }}</span>
              </div>
              <div v-if="recipientType === 'bank' && !isInternational">
                <span class="text-gray-600">Account: </span>
                <span class="font-medium">****{{ accountNumber.slice(-4) }}</span>
              </div>
              <div v-if="recipientType === 'bank' && isInternational && swiftCode">
                <span class="text-gray-600">SWIFT: </span>
                <span class="font-medium">{{ swiftCode }}</span>
              </div>
            </div>
          </div>

          <div v-if="memo" class="p-4 bg-gray-50 rounded-lg">
            <h3 class="font-medium text-gray-800 mb-1">Memo</h3>
            <p class="text-sm">{{ memo }}</p>
          </div>

          <div class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div class="flex justify-between items-center">
              <span class="font-medium text-blue-800">Total Amount</span>
              <span class="font-semibold text-lg text-blue-800">${{ totalAmount.toFixed(2) }}</span>
            </div>
          </div>
        </div>

        <!-- Security Options -->
        <div class="space-y-3">
          <InputCheckbox v-model="requiresAuth" name="requiresAuth">
            Require additional authentication for this transfer
          </InputCheckbox>
          <InputCheckbox v-model="sendNotification" name="sendNotification">
            Send confirmation email to recipient
          </InputCheckbox>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-between pt-6">
          <button
            @click="previousStep"
            class="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Back
          </button>
          <button
            @click="confirmTransfer"
            :disabled="isLoading"
            class="px-8 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <span v-if="isLoading">Sending Transfer...</span>
            <span v-else>Send Money</span>
          </button>
        </div>
      </div>
    </BasicCard>

    <!-- Step 4: Confirmation -->
    <BasicCard v-if="step === 4">
      <div class="text-center space-y-6">
        <div class="flex justify-center">
          <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <check-icon class="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div>
          <h2 class="text-2xl font-semibold text-green-800 mb-2">Transfer Sent!</h2>
          <p class="text-gray-600">
            Your money transfer has been initiated successfully.
          </p>
        </div>

        <div class="bg-gray-50 p-4 rounded-lg">
          <p class="text-sm text-gray-600 mb-2">Transaction ID</p>
          <code class="text-sm font-mono bg-white p-2 rounded border block">
            {{ transactionId }}
          </code>
        </div>

        <div class="space-y-2">
          <p class="font-medium">${{ amount }} sent to {{ recipientName }}</p>
          <p class="text-sm text-gray-600">Transfer fee: ${{ transferFee.toFixed(2) }}</p>
          <p class="text-sm text-gray-500">
            <span v-if="transferMethod === 'instant_transfer'">Arriving instantly</span>
            <span v-else-if="transferMethod === 'bank_transfer'">Arriving in 1-3 business days</span>
            <span v-else-if="transferMethod === 'wire_transfer'">Arriving same day</span>
            <span v-else>Arriving in 1-5 business days</span>
          </p>
        </div>

        <div class="flex justify-center gap-4 pt-6">
          <button
            @click="startNewTransfer"
            class="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Send Another
          </button>
          <button
            @click="$router.push({ name: 'account.fiat' })"
            class="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Back to Accounts
          </button>
        </div>
      </div>
    </BasicCard>
  </div>
</template>

<style scoped lang="scss">

</style>
